import {
  Provider,
  Root,
  Trigger,
  Portal,
  Content,
  Arrow,
} from '@radix-ui/react-tooltip';
import React from 'react';

interface TooltipContentProps {
  enabled?: boolean;
  onDismiss: () => void;
  onTryExample: () => void;
}

const AICtaTooltipContent = ({
  enabled,
  onDismiss,
  onTryExample,
}: TooltipContentProps) => (
  <div className="flex items-center gap-3 p-3 pl-4">
    <img
      height={103}
      width={160}
      src="/assets/common/images/footage_hvlp.png"
      alt="Conversational search preview"
      className="flex-shrink-0 rounded border border-white object-cover"
    />
    <div className="flex flex-col">
      <div className="flex items-center gap-2.5 pb-1">
        <span className="text-base font-semibold text-white">
          {enabled ? 'Conversational Search Enabled!' : 'Conversational Search'}
        </span>
        {!enabled && (
          <span className="rounded bg-gray-700 px-1.5 py-0.5 text-xs font-medium text-white">
            NEW
          </span>
        )}
      </div>
      <p className="text-sm leading-5 text-gray-400">
        Refine your search as you go — click suggestions or keep chatting to
        narrow or expand results.
      </p>
      <div className="mt-2 flex items-center gap-2.5">
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm font-normal text-white hover:underline cursor-pointer bg-transparent border-0 p-0"
        >
          Got It
        </button>
        <button
          type="button"
          onClick={onTryExample}
          className="text-sm font-normal hover:underline cursor-pointer border-0 p-0 bg-gradient-to-r from-yellow-400 via-bubblegum-600 to-bubblegum-400 text-transparent bg-clip-text"
        >
          Try an Example
        </button>
      </div>
    </div>
  </div>
);

const StandardSearchTooltipContent = ({
  onDismiss,
}: Partial<TooltipContentProps>) => (
  <div className="flex items-center gap-3 p-3 pl-4 w-48">
    <div className="flex flex-col">
      <div className="flex items-center gap-2.5 pb-1">
        <span className="text-base font-semibold text-white">
          Standard Search
        </span>
      </div>
      <p className="text-sm leading-5 text-gray-400">
        Switch back to standard search anytime.
      </p>
      <div className="mt-2 flex items-center gap-2.5">
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm font-normal text-white hover:underline cursor-pointer bg-transparent border-0 p-0"
        >
          Switch Back
        </button>
      </div>
    </div>
  </div>
);

interface ConversationalSearchTooltipProps {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  trigger: React.ReactElement;
  onTryExample: () => void;
}

export default function ConversationalSearchTooltip({
  enabled,
  setEnabled,
  trigger,
  onTryExample,
}: ConversationalSearchTooltipProps) {
  const [dismissed, setDismissed] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Capture `enabled` at hover start so toggling mid-hover doesn't swap content
  const [enabledAtHover, setEnabledAtHover] = React.useState(false);

  const isOpen = !dismissed && hovered;

  const cancelClose = (): void => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = (): void => {
    cancelClose();
    if (!hovered) {
      setEnabledAtHover(enabled);
      setDismissed(false);
    }
    setHovered(true);
  };

  const handleMouseLeave = (): void => {
    // add a slight delay before hiding to allow users to move their mouse into the tooltip without it disappearing
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => setHovered(false), 150);
  };

  // cleanup timeout etc on unmount
  React.useEffect(() => cancelClose, []);

  // We need to instantiate a new Provider here to get around BuilderBlock's use of a separate React tree.
  // Builder-rendered tooltips can't access the state/context of the main app (Providers live in the root layout)
  return (
    <Provider>
      <Root
        delayDuration={0}
        open={isOpen}
        onOpenChange={(open) => !open && !hovered && setHovered(false)}
      >
        <Trigger
          asChild
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {trigger}
        </Trigger>
        <Portal>
          <Content
            side="top"
            sideOffset={8}
            className="z-50 max-w-md rounded-xl bg-gray-950 bg-opacity-90 shadow-lg"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {enabledAtHover ? (
              <StandardSearchTooltipContent
                onDismiss={() => setEnabled(false)}
              />
            ) : (
              <AICtaTooltipContent
                enabled={enabled}
                onDismiss={() => setDismissed(true)}
                onTryExample={() => {
                  setEnabled(true);
                  onTryExample();
                }}
              />
            )}
            <Arrow
              style={{
                fill: 'rgba(26, 28, 30, 0.9)',
                clipPath: 'inset(0 -10px -10px -10px)',
              }}
            />
          </Content>
        </Portal>
      </Root>
    </Provider>
  );
}
